/**
 * Cell-parser engine specs for the trips importer templating system.
 * MUST stay JSON-serializable — these are stored per template in the DB.
 *
 * Supports `datetime`, `raw`, and `regex` parsers over a source cell, plus the
 * source-less `constant` and `relativeDate` value generators. The shape (parser ->
 * named parts -> N outputs) is the universal currency that future parser types
 * (split, chaining) and the wide->long checkpoint reshape compose onto.
 */
export type IParserSpec =
    | { type: 'datetime'; format: string } // dayjs token, strict parse
    | { type: 'raw' }
    | { type: 'regex'; pattern: string; flags?: string } // named capture groups → named parts
    | { type: 'constant'; value: string } // same value for every row, ignores the cell
    | { type: 'relativeDate'; days: number; time?: string }; // import date (user timezone) + `days`, at `time` (HH:mm)

export interface IOutputSpec {
    /** Canonical importer field (a key of the importer's ExcelRowData), e.g. 'tripCode', 'scheduledStart'. */
    target: string;
    /** Which named part produced by the parser to read (e.g. 'value'). */
    key: string;
    /** Optional reformat for Date parts, e.g. 'DD/MM/YYYY' or 'HH:mm'. */
    format?: string;
    required?: boolean;
    /** Translates the final value from the customer's wording to QuadMinds' (e.g. carrier or location names). */
    valueMap?: IValueMap;
}

/**
 * Lookup table applied to an output's final value. `from` is compared ignoring case,
 * accents and surrounding spaces. Entries are a list (not an object) because file
 * values may contain dots, which can't be Mongo keys.
 */
export interface IValueMap {
    entries: { from: string; to: string }[];
    /** Value not in the table: `keep` it as is (default) or fail the row with an `error`. */
    unmatched?: 'keep' | 'error';
}

export interface ICellMapping {
    /** Source column header text in the user's file. Omitted for source-less parsers (`constant`, `relativeDate`). */
    source?: string;
    parser: IParserSpec;
    /** Routes the parser's named parts to one or more canonical target fields. */
    outputs: IOutputSpec[];
}

/**
 * Rows → trips. When present, rows sharing the `tripKey` values form one trip, which
 * turns the template from "one source row = one canonical row" into a reshape.
 */
export interface ITripGrouping {
    /** Mapped fields (canonical or `$variables`) whose values identify a trip. */
    tripKey: string[];
    /** `field`: the mapped `tripCode`; `autogenerate`: one code per trip from the organization's sequence. */
    tripCode: { strategy: 'field' | 'autogenerate' };
}

/**
 * One step of the checkpoint plan. Rules are applied in order and each emitted
 * checkpoint takes the next index, so the plan defines the checkpoint sequence.
 */
export interface ICheckpointRule {
    /** Checkpoint type, as named in the trip definition. */
    type: string;
    /** Location code: a fixed value, or a mapped field (canonical or `$variable`). Exactly one of them. */
    location: { fixed?: string; field?: string };
    /**
     * `trip` (default): one checkpoint per trip; every row must agree on the location.
     * `distinct`: one checkpoint per distinct location, in order of first appearance.
     */
    per?: 'trip' | 'distinct';
    /** Skip the checkpoint when its location is empty instead of failing. */
    optional?: boolean;
    /** The rows' order fields land on this checkpoint. At most one rule carries orders. */
    carriesOrders?: boolean;
}

/** High-level intent of the template: what should happen by default for new vs. existing trips. */
export type TripImportMode = 'create' | 'update' | 'upsert';

/** What to do when a tripCode from the file is NOT found in the database. */
export type TripImportNotFoundBehaviour = 'error' | 'create' | 'warn';

/** What to do when a tripCode from the file already exists in the database. */
export type TripImportAlreadyExistsBehaviour = 'error' | 'update' | 'warn';

/** Fully-resolved policy after merging mode defaults with per-field overrides. */
export interface IResolvedImportPolicy {
    onNotFound: TripImportNotFoundBehaviour;
    onAlreadyExists: TripImportAlreadyExistsBehaviour;
}

export interface ICreateTripImportTemplateDto {
    name: string;
    description?: string;
    /**
     * High-level intent. Drives the default values of `onNotFound` / `onAlreadyExists`.
     * - `create`  → onNotFound:'create', onAlreadyExists:'error'
     * - `update`  → onNotFound:'error',  onAlreadyExists:'update'
     * - `upsert`  → onNotFound:'create', onAlreadyExists:'update'  (default)
     */
    mode?: TripImportMode;
    /** Overrides the `onNotFound` default derived from `mode`. */
    onNotFound?: TripImportNotFoundBehaviour;
    /** Overrides the `onAlreadyExists` default derived from `mode`. */
    onAlreadyExists?: TripImportAlreadyExistsBehaviour;
    columnMappings: ICellMapping[];
    grouping?: ITripGrouping;
    /** Checkpoint plan. Requires `grouping`. */
    checkpoints?: ICheckpointRule[];
}

export type IUpdateTripImportTemplateDto = Partial<ICreateTripImportTemplateDto>;
